/**
 * Dashboard "Low stock — reorder list" (Dashboard idea 3): every item below its Min level (5 when none
 * is set) with what the sites need and how many to order. Min can be set right here (PUT /catalog/{id})
 * and an item reserved for a project without leaving the page. "Site demand" is the Sites BOQ still to
 * deliver that nobody has booked (boqDemandByPart) — the same number the Stock tab shows.
 */
import { useState } from 'react';
import { ArrowRight, Gauge, Lock, PackageSearch, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { EmptyState } from '@/components/ui/empty-state';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CardFoot, CardHead, CountPill, InvCard, LinkButton } from './InventoryUi';
import { DEFAULT_MIN_LEVEL, formatQty, locationShort, stockLocations } from './inventoryModel';

const th = 'h-9 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold tracking-[0.02em] text-ink-3';
const td = 'h-[54px] border-b border-line-2 px-3 align-middle';
const num = 'text-right tabular-nums';

export default function ReorderList({ rows = [], minNotSet, onSetMin, onReserve, onOpenStock, onOpenMinLevels, canEdit = true, canReserve = true }) {
  return (
    <InvCard data-testid="inventory-reorder-list">
      <CardHead
        icon={TriangleAlert}
        iconClassName="text-status-amber-ink"
        title="Low stock — reorder list"
        badge={<CountPill tone={rows.length ? 'amber' : 'green'}>{formatNumber(rows.length)}</CountPill>}
        right={<LinkButton onClick={onOpenStock}>Open in Stock <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton>}
      />
      {rows.length ? (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[620px] border-separate border-spacing-0 text-[13px] text-ink">
            <thead>
              <tr>
                <th className={th}>Item</th>
                <th className={cn(th, num)}>Available</th>
                <th className={cn(th, num)}>Min</th>
                <th className={cn(th, num)} title="Sites BOQ still to deliver, minus what is already booked">Site demand</th>
                <th className={cn(th, num, 'text-ink')}>Reorder</th>
                <th className={cn(th, 'w-[104px]')}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const { item } = row;
                const where = stockLocations(item)[0];
                return (
                  <tr key={item.part_number} className="[&:hover>td]:bg-surface-hover" data-testid={`reorder-row-${item.part_number}`}>
                    <td className={cn(td, 'max-w-[280px]')}>
                      <span className="block truncate font-mono text-[12.5px] text-ink" title={item.part_number}>{item.part_number || '—'}</span>
                      <span className="mt-px flex min-w-0 items-center gap-1 text-[11.5px] text-ink-3">
                        {where ? <span className="shrink-0 font-semibold text-brand-ink">{locationShort(where.location)}</span> : null}
                        {where ? <span aria-hidden="true">·</span> : null}
                        {row.issue ? (
                          <span className="flex min-w-0 items-center gap-1 text-status-amber-ink">
                            <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
                            <span className="truncate">{row.issue}</span>
                          </span>
                        ) : (
                          <span className="truncate" title={item.description}>{item.description}</span>
                        )}
                      </span>
                    </td>
                    <td className={cn(td, num, 'font-bold')}>{formatQty(row.available)}</td>
                    <td className={cn(td, num)}>
                      <span className={cn('block', row.min === null ? 'text-ink-3' : 'font-semibold text-ink')}>{formatQty(row.threshold)}</span>
                      <span className="block text-[11px] text-ink-3">
                        {row.min === null ? 'default · ' : ''}
                        <SetMinPopover item={item} current={row.min} onSave={onSetMin} disabled={!canEdit} />
                      </span>
                    </td>
                    <td className={cn(td, num)}>
                      <span className={cn('block', row.short > 0 ? 'font-bold text-status-red-ink' : row.demand > 0 ? 'text-ink' : 'text-ink-3')}>{formatQty(row.demand)}</span>
                      {row.short > 0 ? <span className="block text-[11.5px] text-status-red-ink">Short {formatQty(row.short)}</span> : null}
                    </td>
                    <td className={cn(td, num)} data-testid={`reorder-qty-${item.part_number}`}>
                      <b className="font-bold text-ink">{formatQty(row.reorder)}</b>{' '}
                      <span className="text-[12px] text-ink-3">{row.reorder === 1 ? 'pc' : 'pcs'}</span>
                    </td>
                    <td className={cn(td, 'pl-1 pr-3 text-right')}>
                      {canReserve && row.demand > 0 && row.available > 0 ? (
                        <Button type="button" size="xs" variant="outline" className="text-ink" onClick={() => onReserve?.(item)} data-testid={`reorder-reserve-${item.part_number}`}>
                          <Lock aria-hidden="true" /> Reserve
                        </Button>
                      ) : (
                        <RowActions
                          size="sm"
                          label={`Actions for ${item.part_number || 'item'}`}
                          items={[
                            canReserve ? { label: 'Reserve for a project…', icon: Lock, onSelect: () => onReserve?.(item), disabled: row.available <= 0 } : null,
                            { label: 'Open in Stock', icon: PackageSearch, onSelect: onOpenStock }
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={Gauge}
          title="Nothing is below its min level"
          text={`Items show up here when what is available drops below their Min level (${DEFAULT_MIN_LEVEL} when none is set).`}
        />
      )}
      <CardFoot>
        <span>Low = available below Min ({DEFAULT_MIN_LEVEL} when not set) · Reorder = Min − Available · Site demand = Sites BOQ not yet delivered or booked</span>
        {minNotSet ? (
          <LinkButton onClick={onOpenMinLevels} className="text-[12.5px]" data-testid="reorder-set-min-levels">
            Set min levels ({formatNumber(minNotSet)} {minNotSet === 1 ? 'item has' : 'items have'} none)
          </LinkButton>
        ) : null}
      </CardFoot>
    </InvCard>
  );
}

/** "Set" link → a small popover with the Min level field. Lines without a catalog record cannot have one. */
export function SetMinPopover({ item, current, onSave, disabled }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const noCatalog = !item?.catalog_id;
  const blocked = disabled || noCatalog;

  const openWith = (next) => {
    setOpen(next);
    if (next) {
      setValue(current ? String(current) : '');
      setError('');
    }
  };

  const save = async () => {
    const number = value === '' ? 0 : Number(value);
    if (!Number.isFinite(number) || number < 0 || !Number.isInteger(number)) {
      setError('Enter a whole number, 0 or more');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave?.(item, number);
      setOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the min level'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={openWith}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={blocked}
          title={noCatalog ? 'No catalog record — fix the part number first' : undefined}
          className="font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-ink-4 disabled:no-underline"
          aria-label={`Set min level for ${item?.part_number || 'item'}`}
          data-testid={`set-min-${item?.part_number}`}
        >
          Set
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-xl border-line bg-surface p-3 text-ink shadow-overlay">
        <form onSubmit={(event) => { event.preventDefault(); save(); }} className="space-y-2">
          <label className="block">
            <span className="mb-1 block text-[12px] font-semibold text-ink-2">Min stock level · <span className="font-mono">{item?.part_number}</span></span>
            <Input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              autoFocus
              value={value}
              placeholder={`Default ${DEFAULT_MIN_LEVEL}`}
              onChange={(event) => setValue(event.target.value)}
              className="h-9"
              data-testid="set-min-input"
            />
          </label>
          <p className="text-[11.5px] text-ink-3">Low-stock alert below this · empty = default {DEFAULT_MIN_LEVEL}</p>
          {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="set-min-save">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
